import {
  IAnalytics,
  PageData,
  UserData,
  RequiredUserData,
  IdentifyData,
  TrackData,
  PurchaseData,
  VideoFormatInfo,
  LoggingData,
  RequiredLeadData,
  PurchaseValueCurrencyEnum
} from "typings/analytics-types"

export enum VideoConstraintsEnum {
  fileTooBig = "fileTooBig",
  videoTooLong = "videoTooLong",
  uploadTimeout = "uploadTimeout",
  invalidFormat = "invalidFormat"
}

const LOGGING = false

/**
 * For these methods make sure the methods exist. For example, during
 * ssr/react-snap rendering they don't always exist.
 */
const page = (pageData: PageData) => {
  if (window?.analytics && typeof window?.analytics.page === "function")
    window.analytics.page(window.location.pathname, pageData)
}

const track = (name: string, properties?: TrackData) => {
  if (window?.analytics && typeof window?.analytics.track === "function")
    window.analytics.track(name, { ...properties, timeStamp: Date.now() })
}

const identify = (userId: string, properties?: IdentifyData) => {
  if (window?.analytics && typeof window?.analytics.identify === "function")
    window.analytics.identify(userId, properties)

  /* track user ids in hotjar */
  if (window?.hj && typeof window?.hj === "function") window.hj("identify", userId)
}

const logMessage = ({ event, logData }: LoggingData, ...rest: any[]) => {
  if (LOGGING) {
    console.log(
      "%c [Cove logging]: ",
      "color: #74c0b1;",
      `${event} \n ${logData ? JSON.stringify(logData, null, 2) : ""}`,
      ...rest
    )
  }
}

class Analytics implements IAnalytics {
  onPageLoad() {
    const pageData: PageData = {
      path: window?.location.pathname,
      url: window?.location.href
    }

    logMessage({ event: "onPageLoad." }, pageData)

    page(pageData)
  }

  onLeadStep(user: RequiredLeadData) {
    logMessage({ event: "LeadStep.", logData: user })

    if (user) {
      const { id, ...restUser } = user

      identify(id.toString(), { server_source: window?.location.host })
      track("Lead", restUser)
    }
  }

  onRegistration(user: RequiredUserData) {
    logMessage({ event: "Registered.", logData: user })

    if (user) {
      const { id, ...restUser } = user

      identify(id.toString(), { server_source: window?.location.host })
      track("CompleteRegistration", restUser)
    }
  }

  onSubscribe(user: RequiredUserData, purchase: PurchaseData) {
    if (user && purchase.productIds) {
      logMessage({ event: `onSubscribe: ${user.id}. productIds: ${purchase.productIds}` })

      identify(user.id.toString())
      track("Purchase", {
        productIds: purchase.productIds,
        drugCategories: purchase.drugCategories,
        needConsulation: purchase.needConsulation,
        value: purchase.value,
        currency: PurchaseValueCurrencyEnum.USD
      })

      if (purchase.firstPurchase) {
        track("FirstPurchase", {
          purchaseDate: new Date().toString(),
          nextBillingDate: purchase.nextBillingDate,
          eCommerceFlow: purchase.eCommerceFlow,
          value: purchase.value,
          currency: PurchaseValueCurrencyEnum.USD
        })
      }

      if (purchase.eCommerceFlow && purchase.cartItems) {
        track("EcommerceCartItems", {
          cartItems: purchase.cartItems
        })
      }
    }
  }

  onAddUpsellProduct(user: UserData, productId: number) {
    logMessage({ event: `Add upsell product with productId ${productId}`, logData: user })

    if (user) {
      track("AddUpsellMedication", { id: user.id, productId })
    }
  }

  onNotEligibleUser(user?: UserData) {
    logMessage({ event: "Add product frequency.", logData: user })

    if (user) {
      track("NotEligibleUser", user)
    }
  }

  onStartVideoRecording(user?: UserData) {
    logMessage({ event: "Video recording started.", logData: user })

    if (user) {
      track("StartVideoRecording")
    }
  }

  onStartVideoUpload(user?: UserData) {
    logMessage({ event: "Video upload started.", logData: user })

    if (user) {
      track("StartVideoUpload")
    }
  }

  onPhotoIdUpload(user?: UserData) {
    logMessage({ event: "Photo ID finished uploading. Onboarding Finished.", logData: user })

    if (user) {
      track("OnboardingFinished")
    }
  }

  onVideoConstraintsFailed(reason: VideoConstraintsEnum, data?: VideoFormatInfo) {
    logMessage({ event: `Video constraints failed with ${reason}.`, logData: data })

    track("videoConstraintsFailed", { reason, ...data })
  }

  onUserVersionFunneled(user?: UserData, funnel?: string) {
    logMessage({ event: `User was funneled into ${funnel}.`, logData: user })

    if (user && user.id) {
      identify(user.id.toString(), {
        server_source: window?.location.host,
        funnel: funnel
      })
    }
  }

  onVideoSectionStarted(user?: UserData) {
    logMessage({ event: "Video section started.", logData: user })

    if (user) {
      track("VideoSectionStarted")
    }
  }
}

export default new Analytics()
